import { computed, observable, action } from 'mobx';
import get from 'lodash/get';
import { performanceCheck as checkPerformance } from '../utils/performanceCheck';
import { ShareClass } from '../models/entity';
import formatter from '../utils/formatter';
import getDataPoint from '../utils/getDataPoint';
import { stripHtml } from '../utils';
import { replacePlaceholders } from '../utils/replacePlaceholders';
import { ColumnConfig } from '../models/configuration/explorer/ColumnConfig';
import { HistoricDataRequestConfig } from '../models/configuration/HistoricDataRequestConfig';
import { getDisclaimers } from '../utils/disclaimers';
import { DataStore } from './DataStore';

export class HistoryStore {
    @observable.ref shareClass: ShareClass;
    @observable.ref dataStore: DataStore;

    constructor(store: DataStore) {
        this.dataStore = store;
    }

    getPriceHistory = async (columnConfig, clientCode) => {
        const {
            data,
            sort,
            columns,
            disclaimerTypes,
            requestBody,
        } = columnConfig;

        const headings = columns.map(c => `"${c.label}"`).join(',');

        const requestConfig: HistoricDataRequestConfig = {
            clientCode,
            data,
            columns,
            requestBody,
        };

        const result = await this.dataStore.getHistoricData(requestConfig);
        if (result && result.length > 0) {
            this.shareClass = result[0];
            const dataSet = getDataPoint({
                dataStore: this.dataStore,
                shareClass: this.shareClass,
                dataConfig: { ...data, sort },
                locale: this.locale,
                fundCurrency: this.fundCurrency,
                defaultNoDataPlaceholder: this.noDataPlaceholder,
                hasPerformance: this.hasPerformance,
            });

            const disclaimers = this.disclaimerByType(disclaimerTypes)
                .map(d => `"${stripHtml(d)}"`)
                .join('\n');

            let csv = dataSet.map(d => {
                return columns
                    .map(column => {
                        let columnValue;
                        if (column.data) {
                            columnValue = getDataPoint({
                                dataStore: this.dataStore,
                                shareClass: this.shareClass,
                                dataConfig: column.data,
                                locale: this.locale,
                                fundCurrency: this.fundCurrency,
                                defaultNoDataPlaceholder: this
                                    .noDataPlaceholder,
                                hasPerformance: this.hasPerformance,
                            });
                        }
                        if (column.dataKey) {
                            const value = get(
                                d,
                                column.dataKey,
                                this.noDataPlaceholder
                            );
                            columnValue = column.format
                                ? formatter({
                                      value,
                                      format: column.format,
                                      dataStore: this.dataStore,
                                      type: column.format.type,
                                      locale: this.locale,
                                      currency: this.fundCurrency,
                                      options: column.format.options,
                                  })
                                : value;
                        }
                        return `"${columnValue}"`;
                    })
                    .join(',');
            });

            csv.unshift(headings);
            csv.push(disclaimers);
            csv = csv.join('\n');
            const filename = this.getDownloadFilename(columnConfig);
            this.dataStore.downloadCsv(csv, filename);
        }
    };

    getDownloadFilename = (
        columnConfig: ColumnConfig,
        extension: string = '.csv'
    ) => {
        const defaultFilename = 'historic_prices';
        let response = `${defaultFilename}${extension}`;
        const { downloadFilename } = columnConfig;
        if (downloadFilename) {
            const { text, dataFields } = downloadFilename;
            const data = Object.keys(dataFields || {}).reduce((acc, key) => {
                const value = dataFields[key];
                const { dataLocator, format } = value;
                acc[key] = getDataPoint({
                    dataStore: this.dataStore,
                    shareClass: this.shareClass,
                    dataConfig: dataLocator,
                    locale: this.locale,
                    fundCurrency: this.fundCurrency,
                    defaultNoDataPlaceholder: this.noDataPlaceholder,
                    hasPerformance: this.hasPerformance,
                });
                if (acc[key] && format) {
                    acc[key] = formatter({
                        format,
                        dataStore: this.dataStore,
                        type: format.type,
                        locale: this.locale,
                        currency: this.fundCurrency,
                        options: format.options,
                        value: acc[key],
                    });
                }
                return acc;
            }, {});
            response = [
                replacePlaceholders({
                    values: data,
                    text: `${text || defaultFilename}`,
                }),
                extension,
            ].join('');
        }
        return response;
    };

    @computed
    get fundCurrency() {
        return get(
            this.shareClass,
            `properties_pub.${this.dataStore.config.fundCurrencyProperty ||
                'ccy'}.value`
        );
    }

    @computed
    get locale() {
        return this.dataStore.culture;
    }

    @computed
    get noDataPlaceholder() {
        return this.dataStore.config.noDataPlaceholder;
    }

    @computed
    get hasPerformance() {
        const { dataStore, shareClass } = this;
        const { config } = dataStore;
        return checkPerformance({
            dataStore,
            config,
            shareClass,
        });
    }

    @action
    disclaimerByType(disclaimerTypes, shareClass?: ShareClass) {
        const { disclaimers = {} } = shareClass || this.shareClass;
        return getDisclaimers(this.dataStore, disclaimerTypes, disclaimers);
    }
}



// WEBPACK FOOTER //
// ./src/stores/HistoryStore.ts